import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, Grid, IconButton,
  MenuItem, Switch, TextField, Typography, Divider,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AppDispatch } from '../../reduxStore/Store';
import {
  createOrUpdateReportTemplate,
  clearMessages,
  setSelectedReportTemplate,
} from '../../reduxStore/ReportTemplateSlice';
import { fetchSysTypeDocuments } from '../../reduxStore/SysTypeDocumentSlice';
import { ReportTemplate, ReportTemplateOptions, SysTypeDocument } from '../../models/models';
import { TypeRapport } from '../../constants/enums';
import { useSnackbar } from '../SnackbarProvider';
import ModeleWordSection from './ModeleWordSection';

/**
 * Colonnes disponibles par type de rapport.
 * Doit rester en miroir strict de REPORT_TYPE_CONFIG dans :
 *   agence_worker/src/config/reportTypes.config.ts
 */
const COLUMNS_BY_TYPE: Record<string, Array<{ key: string; label: string }>> = {
  DOCUMENT_VENTE: [
    { key: 'numero_doc',      label: 'Référence'      },
    { key: 'date_doc',        label: 'Date'           },
    { key: 'tiers_nom',       label: 'Client/Fourn.'  },
    { key: 'type_document',   label: 'Type'           },
    { key: 'depot_nom',       label: 'Dépôt'          },
    { key: 'departement_nom', label: 'Département'    },
    { key: 'cloture',         label: 'Clôturé'        },
    { key: 'reference',       label: 'Référence ext.' },
    { key: 'total_ht',        label: 'Total HT'       },
    { key: 'total_ttc',       label: 'Total TTC'      },
    { key: 'date_echeance',   label: 'Échéance'       },
  ],
  // Impression d'un document : les colonnes décrivent les LIGNES du document.
  // Décocher une colonne masque la colonne du tableau ET sa ligne dans le pied
  // de totaux (ex : « Total HT » → colonne + ligne « Total HT »).
  IMPRESSION_DOCUMENT: [
    { key: 'code',          label: 'Code'        },
    { key: 'designation',   label: 'Désignation' },
    { key: 'quantite',      label: 'Qté'         },
    { key: 'prix_unitaire', label: 'P.U. HT'     },
    { key: 'remise',        label: 'Remise'      },
    { key: 'tva',           label: 'TVA'         },
    { key: 'total_ht',      label: 'Total HT'    },
    { key: 'total_ttc',     label: 'Total TTC'   },
  ],
  LISTE_ARTICLE: [
    { key: 'code',           label: 'Code'        },
    { key: 'designation',    label: 'Désignation' },
    { key: 'famille_nom',    label: 'Famille'     },
    { key: 'unite',          label: 'Unité'       },
    { key: 'prix_r',         label: 'Prix achat'  },
    { key: 'prix_v',         label: 'Prix vente'  },
    { key: 'tva',            label: 'TVA %'       },
    { key: 'remise',         label: 'Remise %'    },
    { key: 'stock_securite', label: 'Stock mini'  },
    { key: 'track_stock',    label: 'Suivi stock' },
    { key: 'visible',        label: 'Visible'     },
  ],
  STOCK: [
    { key: 'code',           label: 'Code'          },
    { key: 'designation',    label: 'Désignation'   },
    { key: 'famille_nom',    label: 'Famille'       },
    { key: 'stock_reel',     label: 'Stock réel'    },
    { key: 'total_entrees',  label: 'Total entrées' },
    { key: 'total_sorties',  label: 'Total sorties' },
    { key: 'stock_securite', label: 'Stock mini'    },
    { key: 'unite',          label: 'Unité'         },
    { key: 'prix_r',         label: 'Prix achat'    },
    { key: 'prix_v',         label: 'Prix vente'    },
    { key: 'valorisation_achat', label: 'Valo. achat' },
    { key: 'valorisation_vente', label: 'Valo. vente HT' },
    { key: 'valorisation_vente_ttc', label: 'Valo. vente TTC' },
  ],
  MOUVEMENT_STOCK: [
    { key: 'date_doc',         label: 'Date'         },
    { key: 'numero_doc',       label: 'N° doc'       },
    { key: 'type_doc_libelle', label: 'Type'         },
    { key: 'code',             label: 'Code'         },
    { key: 'designation',      label: 'Désignation'  },
    { key: 'departement_nom',  label: 'Département'  },
    { key: 'depot_nom',        label: 'Dépôt'        },
    { key: 'quantite_signee',  label: 'Qté signée'   },
    { key: 'unite',            label: 'Unité'        },
    { key: 'stock_cumule',     label: 'Stock cumulé' },
    { key: 'tiers_nom',        label: 'Tiers'        },
    { key: 'user_login',       label: 'Utilisateur'  },
  ],
  LISTE_REGLEMENT: [
    { key: 'date_reglement',    label: 'Date'             },
    { key: 'numero_reglement',  label: 'Numéro'           },
    { key: 'type_reglement',    label: 'Type'             },
    { key: 'sens_libelle',      label: 'Sens'             },
    { key: 'caisse_nom',        label: 'Caisse'           },
    { key: 'tiers_nom',         label: 'Tiers'            },
    { key: 'nature_nom',        label: 'Nature'           },
    { key: 'mode_paiement_nom', label: 'Mode de paiement' },
    { key: 'montant',           label: 'Montant'          },
    { key: 'reference',         label: 'Référence'        },
  ],
  TIERS: [
    { key: 'nom',             label: 'Nom'           },
    { key: 'prenom',          label: 'Prénom'        },
    { key: 'type',            label: 'Type'          },
    { key: 'telephone',       label: 'Téléphone'     },
    { key: 'email',           label: 'Email'         },
    { key: 'adresse',         label: 'Adresse'       },
    { key: 'departement_nom', label: 'Département'   },
    { key: 'date_creation',   label: 'Date création' },
  ],
  BALANCE_CLIENT: [
    { key: 'nom_complet',      label: 'Nom'             },
    { key: 'telephone',        label: 'Téléphone'       },
    { key: 'solde_anterieur',  label: 'Solde antérieur' },
    { key: 'total_facture',    label: 'Total Facture'   },
    { key: 'total_reglement',  label: 'Total Règlement' },
    { key: 'solde_final',      label: 'Solde'           },
  ],
  BALANCE_FOURNISSEUR: [
    { key: 'nom_complet',      label: 'Nom'             },
    { key: 'telephone',        label: 'Téléphone'       },
    { key: 'solde_anterieur',  label: 'Solde antérieur' },
    { key: 'total_facture',    label: 'Total Facture'   },
    { key: 'total_reglement',  label: 'Total Règlement' },
    { key: 'solde_final',      label: 'Solde'           },
  ],
};

// Orientation par défaut selon le type de rapport.
// Doit rester en miroir de defaultOrientation dans REPORT_TYPE_CONFIG (worker).
const DEFAULT_ORIENTATION_BY_TYPE: Record<string, 'portrait' | 'paysage'> = {
  STOCK:               'paysage',
  MOUVEMENT_STOCK:     'paysage',
  LISTE_REGLEMENT:     'paysage',
  BALANCE_CLIENT:      'paysage',
  BALANCE_FOURNISSEUR: 'paysage',
};

interface Props {
  onCancel: () => void;
}

const defaultOptions = (): ReportTemplateOptions => ({
  logo:        { afficher: false, path: null },
  mise_en_page: { orientation: 'portrait', format_papier: 'A4', couleur_principale: '#1976D2', couleur_secondaire: '#F5F5F5', police: 'Arial', taille_police: 11 },
  entete:       { afficher: true, titre_personnalise: '' },
  pied_de_page: { afficher: true, texte: '', afficher_numero_page: true, afficher_date_generation: true },
  donnees:      { afficher_prix: true, afficher_remise: true, afficher_tva: true, afficher_total: true, arrondir_montants: false, afficher_composants: true },
  colonnes:     [],
});

const ReportTemplateForm: React.FC<Props> = ({ onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const selected = useSelector((state: any) => state.ReportTemplateSlice.selectedReportTemplate) as ReportTemplate | null;

  const sysTypeDocuments = useSelector((state: any) => state.SysTypeDocumentSlice?.sysTypeData ?? []) as SysTypeDocument[];

  const [nom,               setNom]               = useState('');
  const [typeRapport,       setTypeRapport]       = useState<string>('');
  const [typeDocumentCode,  setTypeDocumentCode]  = useState<string>('');
  const [isDefault,         setIsDefault]         = useState(false);
  const [options,           setOptions]           = useState<ReportTemplateOptions>(defaultOptions());

  useEffect(() => {
    if (sysTypeDocuments.length === 0) dispatch(fetchSysTypeDocuments());
  }, []);

  useEffect(() => {
    if (selected) {
      setNom(selected.nom ?? '');
      setTypeRapport(selected.type_rapport ?? '');
      setTypeDocumentCode(selected.type_document_code ?? '');
      setIsDefault(selected.is_default ?? false);
      setOptions(selected.options ?? defaultOptions());
    } else {
      setNom(''); setTypeRapport(''); setTypeDocumentCode(''); setIsDefault(false); setOptions(defaultOptions());
    }
  }, [selected]);

  // Synchroniser les colonnes disponibles quand le type change
  // On fusionne : les colonnes déjà sauvegardées sont conservées,
  // les nouvelles colonnes manquantes sont ajoutées avec visible=true.
  useEffect(() => {
    if (!typeRapport) return;
    const available = COLUMNS_BY_TYPE[typeRapport] ?? [];
    setOptions(prev => {
      const existing   = prev.colonnes ?? [];
      const existingKeys = new Set(existing.map(c => c.key));
      const toAdd = available
        .filter(c => !existingKeys.has(c.key))
        .map((c, i) => ({ ...c, visible: true, ordre: existing.length + i + 1 }));

      // Orientation par défaut du type — appliquée uniquement aux nouveaux templates
      // (un template existant conserve l'orientation choisie par l'utilisateur).
      const defaultOrientation = DEFAULT_ORIENTATION_BY_TYPE[typeRapport] ?? 'portrait';
      const next = (!selected && prev.mise_en_page?.orientation !== defaultOrientation)
        ? { ...prev, mise_en_page: { ...prev.mise_en_page, orientation: defaultOrientation } }
        : prev;

      if (toAdd.length === 0 && existing.length > 0) return next;
      return { ...next, colonnes: [...existing, ...toAdd] };
    });
  }, [typeRapport, selected]);

  const setOpt = (path: string[], value: any) => {
    setOptions(prev => {
      const next = { ...prev } as any;
      let cur = next;
      for (let i = 0; i < path.length - 1; i++) {
        cur[path[i]] = { ...cur[path[i]] };
        cur = cur[path[i]];
      }
      cur[path[path.length - 1]] = value;
      return next;
    });
  };

  const toggleColumn = (key: string, visible: boolean) => {
    setOptions(prev => ({
      ...prev,
      colonnes: (prev.colonnes ?? []).map(c => c.key === key ? { ...c, visible } : c),
    }));
  };

  const addChampExtra = () => {
    setOptions(prev => ({
      ...prev,
      champs_extra: [...(prev.champs_extra ?? []), { label: '', valeur: '' }],
    }));
  };

  const removeChampExtra = (idx: number) => {
    setOptions(prev => ({
      ...prev,
      champs_extra: (prev.champs_extra ?? []).filter((_, i) => i !== idx),
    }));
  };

  const updateChampExtra = (idx: number, field: 'label' | 'valeur', value: string) => {
    setOptions(prev => ({
      ...prev,
      champs_extra: (prev.champs_extra ?? []).map((c, i) => i === idx ? { ...c, [field]: value } : c),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const template: ReportTemplate = {
      ...selected,
      nom,
      type_rapport:        typeRapport || null,
      type_document_code:  typeDocumentCode || null,
      is_default:          isDefault,
      options,
    };
    const result = await dispatch(createOrUpdateReportTemplate(template));
    if (createOrUpdateReportTemplate.fulfilled.match(result)) {
      showSnackbar('Template sauvegardé !', 'success');
      dispatch(clearMessages());
      dispatch(setSelectedReportTemplate(null));
      onCancel();
    } else {
      showSnackbar(result.payload as string, 'error');
    }
  };

  const availableCols = COLUMNS_BY_TYPE[typeRapport] ?? [];
  const templateCols  = options.colonnes ?? [];

  // Même périmètre que l'API (TYPES_RAPPORT_MODELE_WORD) : impression de documents ou template générique
  const modeleWordDisponible = !typeRapport || typeRapport === TypeRapport.IMPRESSION_DOCUMENT || typeRapport === TypeRapport.DOCUMENT_VENTE;
  const modeleWordActif      = modeleWordDisponible && !!options.modele_word?.actif && !!options.modele_word?.fichier_ref;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Grid container spacing={2}>

        {/* Infos générales */}
        <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold">Informations</Typography></Grid>

        <Grid item xs={12} sm={6}>
          <TextField label="Nom du template" value={nom} onChange={e => setNom(e.target.value)} fullWidth required size="small" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select label="Type de rapport" value={typeRapport} onChange={e => setTypeRapport(e.target.value)} fullWidth size="small">
            <MenuItem value=""><em>Tous les types</em></MenuItem>
            {Object.values(TypeRapport).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControlLabel control={<Switch checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />} label="Défaut" />
        </Grid>

        {/* Type de document — uniquement pour les templates de documents de vente */}
        {(!typeRapport || typeRapport === TypeRapport.DOCUMENT_VENTE || typeRapport === TypeRapport.IMPRESSION_DOCUMENT) && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Type de document (optionnel)"
              value={typeDocumentCode}
              onChange={e => setTypeDocumentCode(e.target.value)}
              fullWidth
              size="small"
              helperText="Laisser vide = s'applique à tous les types de documents"
            >
              <MenuItem value=""><em>Tous les types de documents</em></MenuItem>
              {sysTypeDocuments.map(t => (
                <MenuItem key={t.code} value={t.code}>{t.libelle} ({t.code})</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* Modèle Word client — templates d'impression de documents uniquement */}
        {modeleWordDisponible && (
          <>
            <Grid item xs={12}><Divider /><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Modèle Word</Typography></Grid>
            <Grid item xs={12}>
              <ModeleWordSection
                templateId={selected?.id}
                modeleWord={options.modele_word}
                onChange={modele_word => setOptions(prev => ({ ...prev, modele_word }))}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}><Divider /><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Mise en page</Typography></Grid>
        {modeleWordActif && (
          <Grid item xs={12}>
            <Alert severity="info">
              Modèle Word actif : la mise en page, le logo, les couleurs, le pied de page et les colonnes visibles
              ne s'appliquent pas à l'impression des documents (c'est votre fichier Word qui les définit).
              Restent utilisés : le titre personnalisé ({'{titre}'}), « Nombres entiers », « Afficher les composants »
              et les champs supplémentaires ({'{#champs_extra}'}).
            </Alert>
          </Grid>
        )}

        <Grid item xs={6} sm={3}>
          <TextField select label="Orientation" value={options.mise_en_page?.orientation ?? 'portrait'} onChange={e => setOpt(['mise_en_page', 'orientation'], e.target.value)} fullWidth size="small">
            <MenuItem value="portrait">Portrait</MenuItem>
            <MenuItem value="paysage">Paysage</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select label="Format" value={options.mise_en_page?.format_papier ?? 'A4'} onChange={e => setOpt(['mise_en_page', 'format_papier'], e.target.value)} fullWidth size="small">
            {['A4','A3','Letter'].map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Couleur principale" type="color" value={options.mise_en_page?.couleur_principale ?? '#1976D2'} onChange={e => setOpt(['mise_en_page', 'couleur_principale'], e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Couleur secondaire" type="color" value={options.mise_en_page?.couleur_secondaire ?? '#F5F5F5'} onChange={e => setOpt(['mise_en_page', 'couleur_secondaire'], e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select label="Police" value={options.mise_en_page?.police ?? 'Arial'} onChange={e => setOpt(['mise_en_page', 'police'], e.target.value)} fullWidth size="small">
            {['Arial','Helvetica','Times New Roman','Courier New'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Taille police" type="number" value={options.mise_en_page?.taille_police ?? 11} onChange={e => setOpt(['mise_en_page', 'taille_police'], Number(e.target.value))} fullWidth size="small" inputProps={{ min: 7, max: 16 }} />
        </Grid>

        {/* Logo */}
        <Grid item xs={12}><Divider /><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Logo</Typography></Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel control={<Switch checked={options.logo?.afficher ?? false} onChange={e => setOpt(['logo', 'afficher'], e.target.checked)} />} label="Afficher logo" />
        </Grid>
        {options.logo?.afficher && (
          <Grid item xs={12} sm={9}>
            <TextField label="URL du logo (lien externe)" value={options.logo?.path ?? ''} onChange={e => setOpt(['logo', 'path'], e.target.value)} fullWidth size="small" placeholder="https://exemple.com/logo.png" />
          </Grid>
        )}

        {/* En-tête & Pied de page */}
        <Grid item xs={12}><Divider /><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>En-tête & Pied de page</Typography></Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel control={<Switch checked={options.entete?.afficher ?? true} onChange={e => setOpt(['entete', 'afficher'], e.target.checked)} />} label="En-tête" />
        </Grid>
        <Grid item xs={12} sm={9}>
          <TextField label="Titre personnalisé" value={options.entete?.titre_personnalise ?? ''} onChange={e => setOpt(['entete', 'titre_personnalise'], e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel control={<Switch checked={options.pied_de_page?.afficher ?? true} onChange={e => setOpt(['pied_de_page', 'afficher'], e.target.checked)} />} label="Pied de page" />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField label="Texte pied de page" value={options.pied_de_page?.texte ?? ''} onChange={e => setOpt(['pied_de_page', 'texte'], e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControlLabel control={<Switch checked={options.pied_de_page?.afficher_numero_page ?? true} onChange={e => setOpt(['pied_de_page', 'afficher_numero_page'], e.target.checked)} />} label="N° page" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControlLabel control={<Switch checked={options.pied_de_page?.afficher_date_generation ?? true} onChange={e => setOpt(['pied_de_page', 'afficher_date_generation'], e.target.checked)} />} label="Date" />
        </Grid>

        {/* Données */}
        <Grid item xs={12}><Divider /><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Données affichées</Typography></Grid>
        {(['afficher_prix','afficher_remise','afficher_tva','afficher_total'] as const).map(k => (
          <Grid item xs={6} sm={3} key={k}>
            <FormControlLabel
              control={<Checkbox checked={(options.donnees as any)?.[k] ?? true} onChange={e => setOpt(['donnees', k], e.target.checked)} />}
              label={k.replace('afficher_', '').replace('_', ' ')}
            />
          </Grid>
        ))}
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={<Checkbox checked={options.donnees?.arrondir_montants ?? false} onChange={e => setOpt(['donnees', 'arrondir_montants'], e.target.checked)} />}
            label="Nombres entiers (sans décimales)"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={<Checkbox checked={(options.donnees as any)?.afficher_composants ?? true} onChange={e => setOpt(['donnees', 'afficher_composants'], e.target.checked)} />}
            label="Afficher les composants (fabrication)"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={<Checkbox checked={(options.donnees as any)?.afficher_utilisateur ?? false} onChange={e => setOpt(['donnees', 'afficher_utilisateur'], e.target.checked)} />}
            label="Afficher l'auteur du document"
          />
        </Grid>

        {/* Colonnes */}
        {typeRapport && availableCols.length > 0 && (
          <>
            <Grid item xs={12}><Divider /><Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Colonnes visibles</Typography></Grid>
            {availableCols.map(col => {
              const tplCol = templateCols.find(c => c.key === col.key);
              const visible = tplCol ? tplCol.visible : true;
              return (
                <Grid item xs={6} sm={3} key={col.key}>
                  <FormControlLabel
                    control={<Checkbox checked={visible} onChange={e => toggleColumn(col.key, e.target.checked)} />}
                    label={col.label}
                  />
                </Grid>
              );
            })}
          </>
        )}

        {/* Champs extra — uniquement pour les templates de document */}
        {(!typeRapport || typeRapport === TypeRapport.IMPRESSION_DOCUMENT || typeRapport === TypeRapport.DOCUMENT_VENTE) && (
          <>
            <Grid item xs={12}>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">Champs supplémentaires (bas de document)</Typography>
                <IconButton size="small" color="primary" onClick={addChampExtra} title="Ajouter un champ">
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Affichés sous la facture — valeur vide = ligne blanche à remplir à la main (ex : Signature, Nom chauffeur, Cachet…)
              </Typography>
            </Grid>
            {(options.champs_extra ?? []).map((champ, idx) => (
              <Grid item xs={12} key={idx}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Libellé"
                    value={champ.label}
                    onChange={e => updateChampExtra(idx, 'label', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="Ex : Signature réception"
                  />
                  <TextField
                    label="Valeur pré-remplie (optionnel)"
                    value={champ.valeur ?? ''}
                    onChange={e => updateChampExtra(idx, 'valeur', e.target.value)}
                    size="small"
                    sx={{ flex: 2 }}
                    placeholder="Laisser vide = ligne blanche"
                  />
                  <IconButton size="small" color="error" onClick={() => removeChampExtra(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </>
        )}

        {/* Actions */}
        <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="outlined" onClick={onCancel}>Annuler</Button>
          <Button variant="contained" type="submit">Sauvegarder</Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportTemplateForm;
